/**
 * Offline, as a first-class state — F25's half of F14.
 *
 * The pack list is used standing at the boot in a garage, and the trip sheet is
 * read in a car on the R33 with one bar. Both must open. Neither may lie about
 * how old what it is showing is.
 *
 * The shape is deliberately small:
 *
 *  · **Read through a cache.** Every GET that matters is written to
 *    localStorage under its path. A failed fetch falls back to the last good
 *    answer and returns `fresh: false` with the time it was taken — the screen
 *    then says so in words. A cached figure presented as live is the same lie as
 *    an invented one (`07-acceptance.md` rule 3).
 *  · **Writes queue.** A tick made with no signal is kept and replayed when the
 *    phone finds the house again. The pack list's PUT replaces whole state, so a
 *    replay is idempotent by construction — see the route's comment.
 *
 * localStorage rather than IndexedDB or a service worker: the payloads are a few
 * kilobytes, the whole thing has to be readable in one sitting, and a service
 * worker is a cache invalidation problem this household does not need. If the
 * trip book ever carries photographs this has to change.
 */
import { api, ApiError } from "./api";

const PREFIX = "waypoint.offline.";
const QUEUE = "waypoint.offline.queue";

type Entry<T> = { at: string; data: T };

function read<T>(path: string): Entry<T> | null {
  try {
    const raw = localStorage.getItem(PREFIX + path);
    if (!raw) return null;
    const e = JSON.parse(raw) as Entry<T>;
    return e && typeof e.at === "string" ? e : null;
  } catch {
    // A corrupt or full localStorage must not take the screen with it.
    return null;
  }
}

function write<T>(path: string, data: T): void {
  try {
    localStorage.setItem(PREFIX + path, JSON.stringify({ at: new Date().toISOString(), data }));
  } catch {
    /* quota, private mode — the app still works, it just forgets. */
  }
}

/** Connectivity, and whether anything is waiting to go up. */
class Net {
  /** `navigator.onLine` is a poor signal on its own — it only knows about the
   *  adapter, not about whether Cloud Run answered. It is corrected by every
   *  failed and successful call below, which is the part that is actually true. */
  online = $state(typeof navigator === "undefined" ? true : navigator.onLine);
  pending = $state(0);

  constructor() {
    if (typeof window === "undefined") return;
    this.pending = Object.keys(this.readQueue()).length;
    window.addEventListener("online", () => {
      this.online = true;
      void this.flush();
    });
    window.addEventListener("offline", () => (this.online = false));
  }

  private readQueue(): Record<string, unknown> {
    try {
      return JSON.parse(localStorage.getItem(QUEUE) ?? "{}") as Record<string, unknown>;
    } catch {
      return {};
    }
  }

  private writeQueue(q: Record<string, unknown>): void {
    try {
      localStorage.setItem(QUEUE, JSON.stringify(q));
    } catch {
      /* nothing useful to do — the tick stays on screen and is lost on reload. */
    }
    this.pending = Object.keys(q).length;
  }

  /** Queue a whole-state PUT. One entry per path: the newest wins, because the
   *  body is the complete state and an older one is simply wrong. */
  queue(path: string, body: unknown): void {
    const q = this.readQueue();
    q[path] = body;
    this.writeQueue(q);
  }

  async flush(): Promise<void> {
    const q = this.readQueue();
    for (const [path, body] of Object.entries(q)) {
      try {
        await api.put(path, body);
        delete q[path];
        this.writeQueue(q);
      } catch (e) {
        // A 4xx will never succeed on a retry and would otherwise block the
        // queue forever; drop it and let the screen re-read the truth.
        if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
          delete q[path];
          this.writeQueue(q);
          continue;
        }
        this.online = false;
        return;
      }
    }
    if (Object.keys(q).length === 0) this.online = true;
  }
}

export const net = new Net();

export type Loaded<T> = {
  data: T | null;
  /** False when this came out of the cache. The screen must say so. */
  fresh: boolean;
  /** When the cached copy was taken. Null when the data is fresh. */
  at: string | null;
  /** Set only when there is no cache to fall back on. */
  error: string | null;
};

/**
 * GET, falling back to the last good answer.
 *
 * Returns an error only when there is genuinely nothing to show — a failure with
 * a cache behind it is not an error, it is yesterday's paper, and it says which.
 */
export async function loadCached<T>(path: string): Promise<Loaded<T>> {
  try {
    const data = await api.get<T>(path);
    write(path, data);
    net.online = true;
    void net.flush();
    return { data, fresh: true, at: null, error: null };
  } catch (e) {
    // A 401/403 is not a signal problem and must not be answered from a cache —
    // showing a signed-out person the last household member's trip sheet would
    // be a genuine leak.
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      return { data: null, fresh: true, at: null, error: e.message };
    }
    net.online = false;
    const cached = read<T>(path);
    if (cached) return { data: cached.data, fresh: false, at: cached.at, error: null };
    return {
      data: null,
      fresh: true,
      at: null,
      error: e instanceof ApiError ? e.message : "No signal, and nothing saved on this device yet.",
    };
  }
}

/** PUT, or queue it if the call fails. Returns true when it actually went up. */
export async function putOrQueue(path: string, body: unknown): Promise<boolean> {
  try {
    await api.put(path, body);
    net.online = true;
    return true;
  } catch (e) {
    if (e instanceof ApiError && e.status >= 400 && e.status < 500) throw e;
    net.queue(path, body);
    net.online = false;
    return false;
  }
}
